import { isDefined } from '@vueuse/core';
import { isEqual } from 'lodash';

import { SemboDate } from '@libs/sembo-dates';
import type {
  ActivityDetailsPriceCriteria,
  BusinessCriteria,
  ItinerarySearchUserCriteriaDependencies,
} from '@libs/trip-planner-interfaces';

export interface ActivitySearchDates {
  startDate?: Date;
  endDate?: Date;
  selectedDate?: Date;
}

export function getActivityAvailabilitySearchDates(
  datesToSearch: ActivitySearchDates,
  numberOfNights: number
): ActivitySearchDates {
  if (!datesToSearch.selectedDate) {
    return datesToSearch;
  }

  return {
    startDate: datesToSearch.selectedDate,
    endDate: new SemboDate(datesToSearch.selectedDate)
      .add({ days: numberOfNights })
      .get(),
    selectedDate: datesToSearch.selectedDate,
  };
}

export function hasChangedOnlySelectedDate(
  dependencies: ItinerarySearchUserCriteriaDependencies,
  oldDependencies: ItinerarySearchUserCriteriaDependencies
): boolean {
  const {
    selectedDate,
    startSearchDate,
    endSearchDate,
    ...dependenciesWihoutSelectedDate
  } = dependencies;
  const {
    selectedDate: oldSelectedDate,
    startSearchDate: oldStartSearchDate,
    endSearchDate: oldEndSearchDate,
    ...oldDependenciesWihoutSelectedDate
  } = oldDependencies;

  const hasSameSelectedDate =
    (!isDefined(selectedDate) && !isDefined(oldSelectedDate)) ||
    (isDefined(selectedDate) &&
      isDefined(oldSelectedDate) &&
      new SemboDate(selectedDate).isSame(oldSelectedDate));

  const hasSameStartSearchDate =
    (!isDefined(startSearchDate) && !isDefined(oldStartSearchDate)) ||
    (isDefined(startSearchDate) &&
      isDefined(oldStartSearchDate) &&
      new SemboDate(startSearchDate).isSame(oldStartSearchDate));

  const hasSameEndSearchDate =
    (!isDefined(endSearchDate) && !isDefined(oldEndSearchDate)) ||
    (isDefined(endSearchDate) &&
      isDefined(oldEndSearchDate) &&
      new SemboDate(endSearchDate).isSame(oldEndSearchDate));

  const hasChangedSelectedDate = !hasSameSelectedDate;

  return (
    isEqual(
      dependenciesWihoutSelectedDate,
      oldDependenciesWihoutSelectedDate
    ) &&
    hasChangedSelectedDate &&
    hasSameStartSearchDate &&
    hasSameEndSearchDate
  );
}

export function hasChangedToSingleDaySearch(
  dependencies: ItinerarySearchUserCriteriaDependencies,
  oldDependencies: ItinerarySearchUserCriteriaDependencies
): boolean {
  const {
    selectedDate,
    startSearchDate,
    endSearchDate,
    ...nonDateDependencies
  } = dependencies;
  const {
    selectedDate: _oldSelectedDate,
    startSearchDate: _oldStartSearchDate,
    endSearchDate: _oldEndSearchDate,
    ...oldNonDateDependencies
  } = oldDependencies;

  return (
    isDefined(selectedDate) &&
    isDefined(startSearchDate) &&
    isDefined(endSearchDate) &&
    new SemboDate(startSearchDate).isSameDay(selectedDate) &&
    new SemboDate(endSearchDate).isSameDay(selectedDate) &&
    isEqual(nonDateDependencies, oldNonDateDependencies)
  );
}

export function getActivityDetailsPriceCriteria(params: {
  businessCriteria: BusinessCriteria;
  datesToSearch: ActivitySearchDates;
  numberOfNights: number;
  productCode: string;
  sessionId: string;
  skipCache?: boolean;
}): ActivityDetailsPriceCriteria {
  return {
    userCriteria: {
      productCode: params.productCode,
      numberOfNights: params.numberOfNights,
      startDate: params.datesToSearch.startDate,
      endDate: params.datesToSearch.endDate,
      selectedDate: params.datesToSearch.selectedDate,
      sessionId: params.sessionId,
      skipCache: params.skipCache,
    },
    businessCriteria: params.businessCriteria,
  };
}
